import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomModal from "../../CustomComponents/modal/customModal";
import onlineOrderingService from "../../../services/onlineOrderingService";
import { customerLogin } from "../../../app/customerAuthSlice";
import { fetchLoyaltyStatus } from "../../../app/loyaltySlice";
import showErrorToast from "../../CustomComponents/toast/toast";
import { showSuccessToast } from "../../CustomComponents/toast/toast";
import "./LoginModal.css";

const VIEW_LOGIN = "login";
const VIEW_SIGNUP = "signup";
const VIEW_FORGOT = "forgotPassword";

const IconUser = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.75" />
    <path d="M5 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
    <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8 11V8a4 4 0 118 0v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IconPhone = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6.5 4h3l1.5 4.5-2 1.5c1.2 2.4 3.1 4.3 5.5 5.5l1.5-2 4.5 1.5v3c0 .8-.7 1.5-1.5 1.5C10.4 19.5 4.5 13.6 4.5 6 4.5 5.2 5.2 4.5 6 4.5z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinejoin="round"
    />
  </svg>
);

const IconKey = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="8" cy="15" r="4" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 15h9m-3-3l3 3-3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const PasswordEyeIcon = ({ visible }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12C3.8 8.5 7.4 6 12 6C16.6 6 20.2 8.5 22 12C20.2 15.5 16.6 18 12 18C7.4 18 3.8 15.5 2 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    {visible && (
      <path d="M4 20L20 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

function FormField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  autoComplete,
  icon,
}) {
  return (
    <div className="login-modal__field">
      <label htmlFor={id} className="form-label login-modal__label">
        {label}
        {required ? <span className="login-modal__required" aria-hidden="true">*</span> : null}
      </label>
      <div className="login-modal__input-wrap">
        {icon ? <span className="login-modal__input-icon" aria-hidden="true">{icon}</span> : null}
        <input
          id={id}
          type={type}
          className={`form-control login-modal__input${icon ? " login-modal__input--with-icon" : ""}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
        />
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  placeholder,
  show,
  onToggle,
  required = false,
  autoComplete,
}) {
  return (
    <div className="login-modal__field">
      <label htmlFor={id} className="form-label login-modal__label">
        {label}
        {required ? <span className="login-modal__required" aria-hidden="true">*</span> : null}
      </label>
      <div className="login-modal__input-wrap login-modal__input-wrap--password">
        <span className="login-modal__input-icon" aria-hidden="true">
          <IconLock />
        </span>
        <input
          id={id}
          type={show ? "text" : "password"}
          className="form-control login-modal__input login-modal__input--with-icon"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="login-modal__toggle-password"
          onClick={onToggle}
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          <PasswordEyeIcon visible={show} />
        </button>
      </div>
    </div>
  );
}

function ViewTabs({ activeView, onLogin, onSignup }) {
  return (
    <div className="login-modal__tabs" role="tablist" aria-label="Account type">
      <button
        type="button"
        role="tab"
        aria-selected={activeView === VIEW_LOGIN}
        className={`login-modal__tab${activeView === VIEW_LOGIN ? " is-active" : ""}`}
        onClick={onLogin}
      >
        Log in
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={activeView === VIEW_SIGNUP}
        className={`login-modal__tab${activeView === VIEW_SIGNUP ? " is-active" : ""}`}
        onClick={onSignup}
      >
        Sign up
      </button>
    </div>
  );
}

export default function LoginModal({
  isOpen,
  onClose,
  restaurantSlug,
  locationId = null,
  onLoginSuccess,
  initialSignupData = {},
}) {
  const dispatch = useDispatch();
  const [view, setView] = useState(VIEW_LOGIN);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [signupFullName, setSignupFullName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupAgreedToUpdates, setSignupAgreedToUpdates] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setSignupFullName(initialSignupData?.fullName || "");
    setSignupPhone(initialSignupData?.phone || "");
    setSignupEmail(initialSignupData?.email || "");
  }, [isOpen, initialSignupData?.fullName, initialSignupData?.phone, initialSignupData?.email]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const resetForm = () => {
    setError("");
    setForgotMessage("");
    setLoginEmail("");
    setLoginPassword("");
    setShowLoginPassword(false);
    setSignupEmail("");
    setSignupPassword("");
    setSignupConfirmPassword("");
    setShowSignupPassword(false);
    setShowSignupConfirmPassword(false);
    setSignupFullName("");
    setSignupPhone("");
    setSignupAgreedToUpdates(false);
    setForgotEmail("");
  };

  const handleClose = () => {
    resetForm();
    setView(VIEW_LOGIN);
    onClose();
  };

  const switchView = (nextView) => {
    setError("");
    setForgotMessage("");
    if (nextView !== VIEW_SIGNUP) {
      setSignupAgreedToUpdates(false);
    }
    setView(nextView);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!loginEmail.trim() || !loginPassword) {
      setError("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      const result = await onlineOrderingService.customerLogin(
        restaurantSlug,
        { email: loginEmail.trim(), password: loginPassword },
        locationId
      );
      if (result.success && result.token && result.customer) {
        dispatch(customerLogin({
          token: result.token,
          refreshToken: result.refreshToken,
          customer: result.customer,
          restaurantSlug,
        }));
        dispatch(fetchLoyaltyStatus({ restaurantSlug, locationId }));
        showSuccessToast("Logged in successfully");
        handleClose();
        onLoginSuccess?.();
      } else {
        setError(result.error || "Login failed");
      }
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!signupEmail.trim() || !signupPassword || !signupFullName.trim() || !signupPhone.trim()) {
      setError("Email, password, full name and phone are required");
      return;
    }
    if (signupPassword !== signupConfirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!signupAgreedToUpdates) {
      setError("Please agree to receive order updates by email or SMS to create an account.");
      return;
    }
    setLoading(true);
    try {
      const result = await onlineOrderingService.customerSignup(
        restaurantSlug,
        {
          email: signupEmail.trim(),
          password: signupPassword,
          fullName: signupFullName.trim(),
          phone: signupPhone.trim(),
        },
        locationId
      );
      if (result.success && result.token && result.customer) {
        dispatch(customerLogin({
          token: result.token,
          refreshToken: result.refreshToken,
          customer: result.customer,
          restaurantSlug,
        }));
        dispatch(fetchLoyaltyStatus({ restaurantSlug, locationId }));
        showSuccessToast("Account created. You are now logged in.");
        handleClose();
        onLoginSuccess?.();
      } else {
        setError(result.error || "Sign up failed");
      }
    } catch (err) {
      setError(err.message || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotMessage("");
    if (!forgotEmail.trim()) {
      setError("Email is required");
      return;
    }
    setLoading(true);
    try {
      const result = await onlineOrderingService.customerForgotPassword(
        restaurantSlug,
        forgotEmail.trim(),
        locationId
      );
      if (result.success) {
        setForgotMessage(result.message || "Password reset email sent. Please check your inbox.");
      } else {
        setError(result.error || "Could not send password reset email");
      }
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const headerMeta = {
    [VIEW_LOGIN]: {
      icon: <IconUser />,
      title: "Welcome back",
      subtitle: "Sign in to continue your order and track deliveries.",
    },
    [VIEW_SIGNUP]: {
      icon: <IconUser />,
      title: "Create account",
      subtitle: "Save your details for faster checkout next time.",
    },
    [VIEW_FORGOT]: {
      icon: <IconKey />,
      title: "Reset password",
      subtitle: "We\u2019ll email you a link to choose a new password.",
    },
  };

  const { icon, title, subtitle } = headerMeta[view];

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      showButton={false}
      showSubmitButton={false}
      showCancelButton={false}
      isCentered={true}
      hideHeader={true}
      contentClassName="login-modal-shell"
      backdropClassName="login-modal-backdrop"
      modalSize="modal-dialog-centered modal-md modal-dialog-scrollable login-modal-dialog"
      maxHeight="min(92vh, 760px)"
      minHeight="auto"
    >
      <div className="login-modal" data-view={view}>
        <button
          type="button"
          className="login-modal__close"
          onClick={handleClose}
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </button>

        <div className="login-modal__hero">
          <div className="login-modal__icon-badge" aria-hidden="true">
            {icon}
          </div>
          <h2 className="login-modal__heading">{title}</h2>
          <p className="login-modal__subheading">{subtitle}</p>
        </div>

        {view === VIEW_FORGOT ? (
          <button
            type="button"
            className="login-modal__back"
            onClick={() => switchView(VIEW_LOGIN)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to log in
          </button>
        ) : (
          <ViewTabs
            activeView={view}
            onLogin={() => switchView(VIEW_LOGIN)}
            onSignup={() => switchView(VIEW_SIGNUP)}
          />
        )}

        {error ? (
          <div className="login-modal__alert login-modal__alert--error" role="alert">
            <span className="login-modal__alert-icon" aria-hidden="true">!</span>
            {error}
          </div>
        ) : null}
        {forgotMessage ? (
          <div className="login-modal__alert login-modal__alert--success" role="status">
            <span className="login-modal__alert-icon" aria-hidden="true">✓</span>
            {forgotMessage}
          </div>
        ) : null}

        <div className="login-modal__body">
          {view === VIEW_LOGIN && (
            <form className="login-modal__form" onSubmit={handleLogin} noValidate>
              <FormField
                id="login-email"
                label="Email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                icon={<IconMail />}
              />
              <PasswordField
                id="login-password"
                label="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Enter your password"
                show={showLoginPassword}
                onToggle={() => setShowLoginPassword((prev) => !prev)}
                required
                autoComplete="current-password"
              />
              <div className="login-modal__forgot-row">
                <button
                  type="button"
                  className="login-modal__text-btn"
                  onClick={() => switchView(VIEW_FORGOT)}
                >
                  Forgot password?
                </button>
              </div>
              <div className="login-modal__actions">
                <button type="submit" className="btn login-modal__submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Logging in...
                    </>
                  ) : (
                    "Log in"
                  )}
                </button>
              </div>
            </form>
          )}

          {view === VIEW_SIGNUP && (
            <form className="login-modal__form" onSubmit={handleSignup} noValidate>
              <div className="login-modal__row-2">
                <FormField
                  id="signup-full-name"
                  label="Full name"
                  type="text"
                  value={signupFullName}
                  onChange={(e) => setSignupFullName(e.target.value)}
                  placeholder="Your name"
                  required
                  autoComplete="name"
                  icon={<IconUser />}
                />
                <FormField
                  id="signup-phone"
                  label="Phone"
                  type="tel"
                  value={signupPhone}
                  onChange={(e) => setSignupPhone(e.target.value)}
                  placeholder="07xxx xxxxxx"
                  required
                  autoComplete="tel"
                  icon={<IconPhone />}
                />
              </div>
              <FormField
                id="signup-email"
                label="Email"
                type="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                icon={<IconMail />}
              />
              <PasswordField
                id="signup-password"
                label="Password"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                placeholder="At least 8 characters"
                show={showSignupPassword}
                onToggle={() => setShowSignupPassword((prev) => !prev)}
                required
                autoComplete="new-password"
              />
              <PasswordField
                id="signup-confirm-password"
                label="Confirm password"
                value={signupConfirmPassword}
                onChange={(e) => setSignupConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                show={showSignupConfirmPassword}
                onToggle={() => setShowSignupConfirmPassword((prev) => !prev)}
                required
                autoComplete="new-password"
              />
              <label className="login-modal__consent">
                <input
                  type="checkbox"
                  className="login-modal__consent-input"
                  checked={signupAgreedToUpdates}
                  onChange={(e) => {
                    setSignupAgreedToUpdates(e.target.checked);
                    if (e.target.checked && error) setError("");
                  }}
                />
                <span className="login-modal__consent-text">
                  By signing up you agree to receive order updates by email or SMS.
                </span>
              </label>
              <div className="login-modal__actions">
                <button
                  type="submit"
                  className="btn login-modal__submit"
                  disabled={loading || !signupAgreedToUpdates}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Creating account...
                    </>
                  ) : (
                    "Create account"
                  )}
                </button>
              </div>
            </form>
          )}

          {view === VIEW_FORGOT && (
            <form className="login-modal__form" onSubmit={handleForgotPassword} noValidate>
              <FormField
                id="forgot-email"
                label="Email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                icon={<IconMail />}
              />
              <div className="login-modal__actions">
                <button type="submit" className="btn login-modal__submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </CustomModal>
  );
}
